import { create } from 'zustand'
import { FEATURES } from '@/lib/features'

/** Every section the settings modal knows how to render. */
export const allSettingsSections = ['profile', 'invites', 'billing', 'security', 'developer'] as const
export type SettingsSection = (typeof allSettingsSections)[number]

/** Sections whose data is fabricated stay out of the list entirely. */
const SECTION_FEATURE: Partial<Record<SettingsSection, boolean>> = {
  profile: FEATURES.SETTINGS_PROFILE,
  security: FEATURES.SETTINGS_SECURITY,
  developer: FEATURES.SETTINGS_DEVELOPER,
}

/**
 * The sections a user can actually reach. This is the single gate: the nav in
 * SettingsLayout, the section the modal renders and the ?section= search param
 * on /workspace/settings all read it, so a hidden section has no entry point.
 */
export const settingsSections: readonly SettingsSection[] = allSettingsSections.filter(
  (section) => SECTION_FEATURE[section] ?? true
)

export const defaultSettingsSection: SettingsSection = settingsSections[0] ?? 'billing'

/** Falls back to the default when a hidden or unknown section is requested. */
export function resolveSettingsSection(section?: SettingsSection): SettingsSection {
  return section && settingsSections.includes(section) ? section : defaultSettingsSection
}

interface SettingsStore {
  isOpen: boolean
  activeSection: SettingsSection
  topUpOpen: boolean
  open: (section?: SettingsSection, options?: { topUp?: boolean }) => void
  close: () => void
  setSection: (section: SettingsSection) => void
  setTopUpOpen: (open: boolean) => void
}

export const useSettingsStore = create<SettingsStore>((set) => ({
  isOpen: false,
  activeSection: defaultSettingsSection,
  topUpOpen: false,
  open: (section, options) => {
    const resolved = resolveSettingsSection(section)
    set({
      isOpen: true,
      activeSection: resolved,
      topUpOpen: resolved === 'billing' && options?.topUp === true,
    })
  },
  close: () => set({ isOpen: false, topUpOpen: false }),
  setSection: (section) => set({ activeSection: resolveSettingsSection(section), topUpOpen: false }),
  setTopUpOpen: (topUpOpen) => set({ topUpOpen }),
}))
